import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { WidgetProps } from '@/lib/widgets/types';
import { useSupportTickets } from '@/hooks/use-support-tickets';
import { Ticket, Clock, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';

export function SupportTicketsWidget({ isEditing = false }: WidgetProps) {
  const { slug } = useParams<{ slug: string }>();
  const { companies, user } = useAuth();
  
  // Get company ID from current slug
  const companyId = companies.find(c => c.slug === slug)?.id;
  
  const { data: tickets = [], isLoading } = useSupportTickets(companyId, user?.id);
  
  const recentTickets = tickets.slice(0, 4);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return { variant: 'destructive' as const, className: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800' };
      case 'high': return { variant: 'default' as const, className: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800' };
      case 'medium': return { variant: 'secondary' as const, className: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800' };
      case 'low': return { variant: 'outline' as const, className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600' };
      default: return { variant: 'outline' as const, className: '' };
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return { variant: 'destructive' as const, className: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800' };
      case 'in_progress': return { variant: 'default' as const, className: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800' };
      case 'paused': return { variant: 'secondary' as const, className: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800' };
      case 'resolved': return { variant: 'outline' as const, className: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800' };
      case 'closed': return { variant: 'secondary' as const, className: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600' };
      default: return { variant: 'outline' as const, className: '' };
    }
  };

  const translateStatus = (status: string) => {
    switch (status) {
      case 'open': return 'Aberto';
      case 'in_progress': return 'Em Andamento';
      case 'paused': return 'Pausado';
      case 'resolved': return 'Resolvido';
      case 'closed': return 'Fechado';
      default: return status;
    }
  };

  const translatePriority = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return priority;
    }
  };

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Ticket className="h-4 w-4" />
            Chamados de Suporte
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Ticket className="h-4 w-4" />
          Meus Chamados
          {tickets.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {tickets.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 pb-6">
        <ScrollArea className="h-full max-h-[240px]">
          {recentTickets.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Nenhum chamado recente
            </div>
          ) : (
            <div className="space-y-1">
              {recentTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="flex items-start gap-3 p-3 hover:bg-muted/50 transition-colors"
                >
                  <Ticket className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                   <div className="flex-1 space-y-2">
                     <div className="space-y-1">
                       <p className="text-sm font-medium leading-none">
                         #{ticket.ticket_number} - {ticket.title || 'Sem título'}
                       </p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge 
                            variant={getStatusColor(ticket.status).variant} 
                            className={`text-xs px-2 py-0.5 ${getStatusColor(ticket.status).className}`}
                          >
                            {translateStatus(ticket.status)}
                          </Badge>
                          <Badge 
                            variant={getPriorityColor(ticket.priority).variant} 
                            className={`text-xs px-2 py-0.5 ${getPriorityColor(ticket.priority).className}`}
                          >
                            {translatePriority(ticket.priority)}
                          </Badge>
                        </div>
                     </div>
                     <div className="flex items-center gap-2 text-xs text-muted-foreground">
                       <Clock className="h-3 w-3" />
                       {formatDistanceToNow(new Date(ticket.created_at), { 
                         addSuffix: true, 
                         locale: ptBR 
                       })}
                     </div>
                   </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}